import { clamp, smoothstep } from '@/core/rng';
import { BIOMES } from '@/world/types';
import type { Region } from '@/world/region';
import type { EnvField } from '@/world/env';
import type { Overlay } from '@/world/overlay';
import type { StrainReads } from '@/world/strainReads';
import type { StrainTraits } from '@/world/strain';
import { DAY } from '@/sim/clock';
import { SOCIAL_CELL_SIZE, socialCenter, socialKey, socialNeighbors } from './socialGrid';

/**
 * The habitat sampler: what a square kilometre offers to whoever lives on it.
 *
 * This is a cheap summary over the models the world already has — the Region
 * baseline, the EnvField's remembered deviations, the Overlay's scars. It
 * invents nothing of its own: if the land weakens, forage falls here because
 * the same field the fire reads says so, not because a second ecology drifted
 * out of sync with the first.
 *
 * Readings are canonical per world-day: a query anywhere inside a day reads
 * the fields at that day's midnight. So a cache hit and a cold recompute agree
 * to the bit, and catching up after a long absence sees exactly the numbers
 * live play saw. No wall clock, no randomness — time is always passed in.
 */

/** What a social cell offers. All values 0..1. */
export interface SocialHabitat {
  landFraction: number;
  waterAccess: number;
  foragePotential: number;
  woodyBiomass: number;
  shelterQuality: number;
  fireRisk: number;
  instability: number;
  settlementSuitability: number;
}

/** A fixed 4×4 lattice of sample points per cell — enough to see a coastline. */
const LATTICE = 4;
/** Metres between the two height samples that measure local slope. */
const SLOPE_STEP = 64;
/** Cache bound: beyond this, the oldest reading is dropped and recomputed on demand. */
const CACHE_MAX = 4096;

const OCEAN_HABITAT: Omit<SocialHabitat, 'waterAccess'> = {
  landFraction: 0, foragePotential: 0, woodyBiomass: 0,
  shelterQuality: 0, fireRisk: 0, instability: 0, settlementSuitability: 0,
};

export class HabitatSampler {
  private readonly cache = new Map<string, SocialHabitat>();
  private readonly traits: StrainTraits = { yield: 1, tolerance: 1, exposure: 1, spread: 1 };

  constructor(
    private readonly region: Region,
    private readonly env: EnvField,
    private readonly overlay: Overlay,
    /**
     * What the ground's strains make of it (2026-08-19, the wake). Optional
     * so a bare sampler in a test reads the pre-strain world exactly; the
     * running Simulation always passes one, and every carrying capacity on
     * the island runs through it — which is the whole reason waking this
     * cost `SNAPSHOT_VERSION` 16 and a prehistory regeneration.
     */
    private readonly strains?: StrainReads,
  ) {}

  /** The cell's reading for the world-day containing `now` (world-seconds). */
  at(sx: number, sz: number, now: number): SocialHabitat {
    const dayIndex = Math.floor(now / DAY);
    // the key carries the land-state versions: a mid-day stamp or scar
    // invalidates any reading pinned earlier that day — so a world restored
    // from a save recomputes the same numbers its donor recomputes, instead
    // of the donor coasting on a pin from before the land changed
    const key = `${socialKey(sx, sz)}@${dayIndex}@${this.env.version}:${this.overlay.version}:${this.strains?.version ?? 0}`;
    const hit = this.cache.get(key);
    // copies out, never the cached object — a caller scribbling on its copy
    // must not be able to poison every later read of the same day
    if (hit) return { ...hit };
    const built = this.build(sx, sz, dayIndex * DAY);
    this.cache.set(key, built);
    if (this.cache.size > CACHE_MAX) {
      // FIFO eviction is safe: a reading is a pure function of
      // (cell, day, land-state), so recomputing returns identical numbers
      const oldest = this.cache.keys().next();
      if (!oldest.done) this.cache.delete(oldest.value);
    }
    return { ...built };
  }

  /** One cold reading, always at the day's midnight `t`. */
  private build(sx: number, sz: number, t: number): SocialHabitat {
    const { region, env, overlay } = this;
    let landPts = 0;
    let forage = 0, woody = 0, gentle = 0, risk = 0, freshScar = 0, river = 0;

    for (let j = 0; j < LATTICE; j++) for (let i = 0; i < LATTICE; i++) {
      const wx = (sx + (i + 0.5) / LATTICE) * SOCIAL_CELL_SIZE;
      const wz = (sz + (j + 0.5) / LATTICE) * SOCIAL_CELL_SIZE;
      const site = region.siteAt(wx, wz);
      if (!site.land) continue;
      landPts++;

      const biome = BIOMES[site.biome];
      const e = env.at(wx, wz, t);
      const scar = overlay.scarAt(wx, wz, t);
      // a scar's bite fades as it ages; a healed scar has already returned null
      const scarCut = scar ? scar.sev * (1 - scar.age01) : 0;

      // …and what the ground's own strains make of the biome (all-ones where
      // nothing remarkable grows — the untouched path multiplies by one).
      // yield scales what a stand puts into the world; tolerance RESHAPES the
      // rain response — it lifts the dry-season floor and leaves the wet
      // ceiling exactly where it was, so a drought-holding strain is a strain
      // that keeps producing when the rains fail, not one that outgrows the
      // rain (strain.ts's axis contract, in the one expression it names)
      const st = this.traits;
      if (this.strains) this.strains.plantTraits(site.biome, wx, wz, t, st);
      else { st.yield = 1; st.tolerance = 1; st.exposure = 1; }

      // forage: what the biome grows, watered by rain — then the land's memory:
      // strengthen lifts it, weaken cuts it, a fresh burn takes what was standing
      let f = biome.density * st.yield * (0.3 * st.tolerance + site.rain * (1 - 0.3 * st.tolerance));
      if (e) {
        f += Math.max(0, e.biomass) * 0.5;
        f *= 1 - clamp(-e.biomass, 0, 1) * 0.8;
      }
      forage += clamp(f * (1 - scarCut), 0, 1);

      // woody biomass: only tree-shaped biomes count; fire takes the stand
      if (biome.woody) woody += clamp(biome.density * st.yield * (1 - scarCut), 0, 1);

      // the best water in the cell is what matters — you walk to the stream.
      // log scale because drainage spans orders of magnitude (1 .. thousands)
      river = Math.max(river, clamp(Math.log(Math.max(1, site.flow)) / Math.log(4000), 0, 1));

      // local slope from two height samples: flat ground shelters, cliffs don't
      const dh = region.heightAt(wx + SLOPE_STEP, wz + SLOPE_STEP) - region.heightAt(wx, wz);
      gentle += 1 - smoothstep(0.08, 0.45, Math.abs(dh) / (SLOPE_STEP * Math.SQRT2));

      // fire risk mirrors FireSim.fuelAt exactly: what the fire would read is
      // what the settlers fear — weaken is standing dead fuel, rain is safety,
      // and a flammable strain's country is a country that burns
      let fuel = biome.density * (1 - site.rain * 0.8) * st.exposure;
      if (e) {
        fuel += Math.max(0, -e.biomass) * 0.5;
        fuel *= 1 - clamp(e.moisture, 0, 1) * 0.85;
        fuel *= 1 + clamp(-e.moisture, 0, 1) * 0.4;
      }
      risk += clamp(fuel, 0, 1);

      freshScar += scarCut;
    }

    const landFraction = landPts / (LATTICE * LATTICE);

    // coastline: water among our own points, or failing that the sea in a
    // neighbouring cell — a shoreline can slip between lattice points
    let coastal = 1 - landFraction;
    if (coastal === 0) {
      for (const nb of socialNeighbors(sx, sz)) {
        const c = socialCenter(nb.sx, nb.sz);
        if (!region.siteAt(c.x, c.z).land) { coastal = 0.25; break; }
      }
    }
    const waterAccess = clamp(Math.max(river, smoothstep(0.02, 0.5, coastal)), 0, 1);

    // open water offers fish and nothing else
    if (landPts === 0) return { ...OCEAN_HABITAT, waterAccess };

    const foragePotential = clamp(forage / landPts, 0, 1);
    const woodyBiomass = clamp(woody / landPts, 0, 1);
    const shelterQuality = clamp(woodyBiomass * 0.55 + (gentle / landPts) * 0.45, 0, 1);
    const fireRisk = clamp(risk / landPts, 0, 1);
    const instability = clamp((freshScar / landPts) * 1.25, 0, 1);
    const settlementSuitability = clamp(
      waterAccess * 0.26 + foragePotential * 0.26 + shelterQuality * 0.18 +
      (1 - fireRisk) * 0.14 + landFraction * 0.16, 0, 1);

    return {
      landFraction, waterAccess, foragePotential, woodyBiomass,
      shelterQuality, fireRisk, instability, settlementSuitability,
    };
  }
}
